import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  Chip,
  ListItemButton,
} from '@mui/material';
import { GitHub } from '@mui/icons-material';

const OpenPrsCard = ({
  title = "Open Items",
  items = [],
  loading = false,
  searchPlaceholder = "Search items...",
  onItemClick,
  getItemKey = (item) => item.id,
  getItemName = (item) => item.name,
  getItemCount = (item) => item.count,
  getItemSubtitle = (item) => item.subtitle,
  getAvatarText = (item) => {
    const name = getItemName(item);
    return name ? name.substring(0, 2).toUpperCase() : 'IT';
  },
  avatarColor = '#737373',
  chipColor = '#f7f9fa',
  chipTextColor = '#737373'
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = useMemo(() => items.filter(item => {
    const itemName = getItemName(item);
    if (!itemName) return false;
    return itemName.toLowerCase().includes(searchTerm.toLowerCase());
  }), [items, searchTerm, getItemName]);

  return (
    <Card sx={{
      borderRadius: 1,
      border: '0 solid #fff',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      bgcolor: '#fff',
      height: { xs: 'auto', lg: '100%' },
      minHeight: { xs: 300, sm: 350, lg: 'auto' }
    }}>
      <CardContent sx={{
        p: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        maxHeight: { xs: 250, sm: 300, md: 400 }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          mb: { xs: 2, sm: 3 },
          flexWrap: 'wrap',
          gap: 2
        }}>
          <GitHub fontSize='16px'/>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            color: '#1f1f1f',
            mb: 3,
            fontSize: '1.5rem',
            letterSpacing: '-0.025em',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{
          flexGrow: 1,
          overflow: 'auto',
          maxHeight: { xs: 250, sm: 300, lg: 'none' }
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography
                color="#737373"
                variant="body2"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Loading items...
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ p: 0 , display: 'flex', flexDirection: 'column', gap: 3}}>
              {filteredItems.length > 0 ? filteredItems.map((item, index) => (
                <ListItemButton
                  key={getItemKey(item)}
                  onClick={() => onItemClick && onItemClick(item)}
                  sx={{
                    borderBottom: index < filteredItems.length - 1 ? '1px solid #e2e8f0' : 'none',
                    bgcolor: '#f5f5f580',
                    cursor: onItemClick ? 'pointer' : 'default',
                    borderRadius: 2,
                    py: { xs: 1.5, sm: 3 },
                    px: { xs: 1.5, sm: 3 },
                    '&:hover': {
                      bgcolor: '#f8fafc'
                    }
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 2, sm: 4 },
                    width: '100%',
                  }}>
                    <GitHub fontSize='12px'/>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 400,
                          color: '#1f1f1f',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {getItemName(item)}
                      </Typography>
                      {getItemSubtitle(item) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#737373',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}
                        >
                          {getItemSubtitle(item)}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={getItemCount(item)}
                      size="small"
                      sx={{
                        bgcolor: chipColor,
                        color: chipTextColor,
                        fontWeight: 600,
                        minWidth: { xs: 32, sm: 36 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        height: { xs: 24, sm: 28 }
                      }}
                    />
                  </Box>
                </ListItemButton>
              )) : (
                <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                  <Typography
                    color="#737373"
                    variant="body2"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    No items found.
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default OpenPrsCard;